/**
 * "Rename this file" (#1538) — the office's override on a file's real name.
 *
 * Corbin, 2026-09-16: the ✎ on a Paperwork row used to set a display LABEL,
 * which changed what the list showed and nothing about what downloaded, what
 * rode the customer email, or what the city received. This edits the filename
 * itself; the label lives on as a second, optional field so #819 B8 keeps
 * working rather than quietly disappearing.
 *
 * Validation is mirrored from the server (`services/attachmentRename.ts`) so a
 * bad name is refused under the cursor instead of after a round-trip. The
 * server still enforces every rule — this is a courtesy, not the gate.
 */
import { useEffect, useState } from "react";
import { Dialog } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export const FILE_NAME_MAX = 180;

/** `".pdf"` from `"Passed SN-1 09-16-2026.pdf"`; `""` when there is none. */
export function extensionOf(fileName: string): string {
    const m = fileName.match(/\.([A-Za-z0-9]{1,8})$/);
    return m ? `.${m[1].toLowerCase()}` : "";
}

/**
 * Mirror of the server's `checkRename`. Returns the complaint to show, or null
 * when the name is fine. Kept deliberately in the same order as the server's so
 * the two say the same thing about the same name.
 */
export function renameComplaint(nextRaw: string, current: string): string | null {
    const next = nextRaw.trim().replace(/\s+/g, " ");
    if (next.length === 0) return "A file needs a name.";
    if (next.length > FILE_NAME_MAX) return `Keep it under ${FILE_NAME_MAX} characters.`;
    // eslint-disable-next-line no-control-regex
    if (/[\\/:*?"<>|\x00-\x1F]/.test(next)) return "A filename can't contain \\ / : * ? \" < > or |.";
    if (next.startsWith(".") || next.includes("..")) return 'A filename can\'t start with or contain "..".';
    const currentExt = extensionOf(current);
    if (currentExt && extensionOf(next) !== currentExt) {
        return `Keep the ${currentExt} on the end — changing the file type stops it opening.`;
    }
    return null;
}

export function RenameFileDialog({
    open,
    onClose,
    fileName,
    photoTag,
    /** Shown above the field so the office knows which of two look-alikes this is. */
    provenance,
    /**
     * Set when the file's name is normally derived from the job's own record —
     * the warning the mockup calls "the record still says…". Renaming does not
     * change the result, and the next upload for that device gets named off the
     * record again, so the dialog says so rather than letting a rename look
     * like a fix.
     */
    derivedNameNote,
    saving,
    onSave,
}: {
    open: boolean;
    onClose: () => void;
    fileName: string;
    photoTag?: string | null;
    provenance?: string | null;
    derivedNameNote?: string | null;
    saving?: boolean;
    onSave: (next: { fileName?: string; photoTag?: string | null }) => void;
}) {
    const [name, setName] = useState(fileName);
    const [label, setLabel] = useState(photoTag ?? "");

    // Re-seed whenever the dialog opens on a different row — a stale name in
    // the box is how you rename the wrong file.
    useEffect(() => {
        if (open) {
            setName(fileName);
            setLabel(photoTag ?? "");
        }
    }, [open, fileName, photoTag]);

    const nameChanged = name.trim().replace(/\s+/g, " ") !== fileName;
    const labelChanged = (label.trim() || null) !== (photoTag ?? null);
    const complaint = nameChanged ? renameComplaint(name, fileName) : null;
    const canSave = !saving && (nameChanged || labelChanged) && !complaint;

    const submit = () => {
        if (!canSave) return;
        onSave({
            fileName: nameChanged ? name.trim().replace(/\s+/g, " ") : undefined,
            photoTag: labelChanged ? label.trim() || null : undefined,
        });
    };

    return (
        <Dialog open={open} onClose={onClose} title="Rename this file">
            <div className="space-y-4 px-5 py-4">
                <p className="text-sm text-muted-foreground">
                    This changes the real filename — what downloads, what rides the customer email, and what the city
                    sees. The old name is kept in the job log.
                </p>
                {provenance && <p className="text-xs text-muted-foreground">{provenance}</p>}

                <div className="space-y-1.5">
                    <Label htmlFor="rename-file-name">Filename</Label>
                    <Input
                        id="rename-file-name"
                        value={name}
                        autoFocus
                        spellCheck={false}
                        onChange={(e) => setName(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") submit();
                        }}
                        aria-invalid={complaint ? true : undefined}
                        aria-describedby={complaint ? "rename-file-error" : undefined}
                    />
                    {complaint ? (
                        <p id="rename-file-error" className="text-xs font-semibold text-destructive">
                            {complaint}
                        </p>
                    ) : (
                        nameChanged && (
                            <p className="text-xs text-muted-foreground">
                                Was: <span className="line-through">{fileName}</span>
                            </p>
                        )
                    )}
                </div>

                {derivedNameNote && (
                    <div className="rounded-lg border border-amber-500/40 bg-amber-50 p-3 text-xs text-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
                        {derivedNameNote}
                    </div>
                )}

                <div className="space-y-1.5">
                    <Label htmlFor="rename-file-label">Label (optional)</Label>
                    <Input
                        id="rename-file-label"
                        value={label}
                        maxLength={60}
                        placeholder="Shown instead of the filename in lists"
                        onChange={(e) => setLabel(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") submit();
                        }}
                    />
                </div>

                <div className="flex justify-end gap-2 pt-1">
                    <Button variant="outline" onClick={onClose} disabled={saving}>
                        Cancel
                    </Button>
                    <Button onClick={submit} disabled={!canSave}>
                        {saving ? "Saving…" : "Save"}
                    </Button>
                </div>
            </div>
        </Dialog>
    );
}
